import styled from "@emotion/styled";

import { RadioButton, ShadowToken, SpacingToken, Tag, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { BillingCycle, ISubscription, Plan } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";
import { addCommas } from "utils/string";

export interface IProps {
  subscription: ISubscription | null;
  newSubscription: ISubscription;
  onBillingCycleChange(billingCycle: BillingCycle): void;
}

const getBillingCycleLabel = (billingCycle?: BillingCycle): string => {
  switch (billingCycle) {
    case BillingCycle.Monthly:
      return "Monthly";
    case BillingCycle.Annually:
      return "Annually";
    case BillingCycle.ThreeYears:
      return "Every 3 years";
    case BillingCycle.TwoYears:
      return "Every 2 years";
    case BillingCycle.SemiAnnually:
      return "Semi-annually";
    case BillingCycle.Quarterly:
      return "Quarterly";
    default:
      return "Custom";
  }
};

// difference in price between monthly and annual payment per agent
const getDifferenceInAgentPerPlan = (plan: Plan): number => {
  switch (plan) {
    case Plan.Team:
      return 5;
    case Plan.Business:
      return 9;
    default:
      return 0;
  }
};

const calculateSavePerYear = (subscription: ISubscription): number => {
  const { plan, agents } = subscription;
  return getDifferenceInAgentPerPlan(plan) * agents * 12;
};

export const BillingCycleRow = ({ subscription, newSubscription, onBillingCycleChange }: IProps): JSX.Element => {
  const paidMonthly = newSubscription.billingCycle === BillingCycle.Monthly;
  const isCustomPlan = !paidMonthly && newSubscription.billingCycle !== BillingCycle.Annually;

  const save = calculateSavePerYear(newSubscription);
  const shouldDisplaySave =
    save > 0 &&
    (!isCustomPlan ||
      (isCustomPlan &&
        (newSubscription.billingCycle === BillingCycle.Annually ||
          newSubscription.billingCycle === BillingCycle.TwoYears ||
          newSubscription.billingCycle === BillingCycle.ThreeYears)));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleBillingCycleChangeToMonthly = () => onBillingCycleChange(BillingCycle.Monthly);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleBillingCycleChangeToAnnually = () =>
    onBillingCycleChange(isCustomPlan ? newSubscription.billingCycle : BillingCycle.Annually);

  return (
    <Container customPlanLabel={subscription?.plan === Plan.Enterprise}>
      <Text bold>Billing cycle</Text>
      {subscription?.plan === Plan.Enterprise ? (
        <Text>{getBillingCycleLabel(subscription?.billingCycle)}</Text>
      ) : (
        <Flex column gap={withVar(SpacingToken.Spacing2)}>
          <Flex gap={withVar(SpacingToken.Spacing2)}>
            <RadioButton
              checked={!paidMonthly}
              onClick={handleBillingCycleChangeToAnnually}
              data-testid="radio-billing-cycle-annually"
            >
              {isCustomPlan ? <>{getBillingCycleLabel(subscription?.billingCycle)}</> : <>Annually</>}
            </RadioButton>
            {shouldDisplaySave && (
              <Tag size="small" kind="success">
                <Text size="sm" bold>
                  Save ${addCommas(save)}/yr
                </Text>
              </Tag>
            )}
          </Flex>
          <RadioButton
            disabled={isCustomPlan}
            checked={paidMonthly}
            onClick={handleBillingCycleChangeToMonthly}
            data-testid="radio-billing-cycle-monthly"
          >
            Monthly
          </RadioButton>
        </Flex>
      )}
    </Container>
  );
};

const Container = styled.div<{ customPlanLabel: boolean }>`
  display: flex;
  padding: ${withVar(SpacingToken.Spacing4)} 0;
  gap: ${withVar(SpacingToken.Spacing12)};
  box-shadow: ${withVar(ShadowToken.DividerBottom)};
  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.customPlanLabel && "justify-content: space-between;"
  }
`;
